import React, { useCallback, useEffect, useState } from 'react';
import {
  getMailboxConfig,
  saveMailboxConfig,
  testMailboxConnection,
  syncMailboxNow,
  getMailboxSyncStatus,
} from '../../services/emailInboxApi';
import ProviderGuidePanel, { PROVIDERS, getProviderDefaults, guessProviderId } from './MailboxConfigHelp';

const DEFAULT_FORM = {
  enabled: false,
  auth_method: 'basic',
  imap_host: '',
  imap_port: 993,
  imap_username: '',
  imap_password: '',
  imap_use_ssl: true,
  imap_folder: 'INBOX',
  poll_interval_minutes: 5,
  poll_interval_seconds: null,
  mark_seen_after_ingest: true,
  classification_max_retries: 3,
  oauth_tenant_id: '',
  oauth_client_id: '',
  oauth_client_secret: '',
  reply_smtp_override: false,
  reply_smtp_host: '',
  reply_smtp_port: 587,
  reply_smtp_username: '',
  reply_smtp_password: '',
  reply_smtp_from_email: '',
  reply_smtp_use_tls: true,
  sender_filter: [],
};

const STEP_ORDER = ['provider', 'connect', 'advanced', 'review'];
const STEP_LABELS = { provider: 'Provider', connect: 'Connect', advanced: 'Advanced', review: 'Review & Save' };

const ERROR_GUIDANCE = [
  { match: /imap login failed/i, tip: 'Check the username and password. For Gmail with 2-Step Verification, use an App Password. For Microsoft 365, confirm IMAP is enabled for this mailbox in the Exchange Admin Center.' },
  { match: /imap host unreachable/i, tip: 'The host/port could not be reached. Double-check the hostname and port, and confirm your network allows outbound IMAP traffic.' },
  { match: /oauth_tenant_id, oauth_client_id, and oauth_client_secret are required/i, tip: 'Fill in Tenant ID, Client ID, and Client Secret from your Azure AD app registration.' },
  { match: /imap_password is required/i, tip: 'Enter a password (or App Password) for Basic Auth, or switch to OAuth2 above.' },
  { match: /oauth2 token request failed/i, tip: 'Azure AD rejected the token request — check the Client Secret hasn\'t expired and the app has IMAP.AccessAsApp permission with admin consent.' },
  { match: /imap_host and imap_username are required/i, tip: 'Enter both the IMAP host and the mailbox username/email address above.' },
];

function guidanceForError(message) {
  if (!message) return null;
  const hit = ERROR_GUIDANCE.find((g) => g.match.test(message));
  return hit ? hit.tip : 'Double-check the settings above and try again, or see the setup guide for this provider.';
}

function buildFormFromConfig(config) {
  if (!config) return { ...DEFAULT_FORM };
  return {
    enabled: Boolean(config.enabled),
    auth_method: config.auth_method === 'oauth2' ? 'oauth2' : 'basic',
    imap_host: String(config.imap_host || ''),
    imap_port: Number(config.imap_port || 993),
    imap_username: String(config.imap_username || ''),
    imap_password: String(config.imap_password || ''),
    imap_use_ssl: Boolean(config.imap_use_ssl !== false),
    imap_folder: String(config.imap_folder || 'INBOX'),
    poll_interval_minutes: Number(config.poll_interval_minutes || 5),
    poll_interval_seconds: config.poll_interval_seconds != null ? Number(config.poll_interval_seconds) : null,
    mark_seen_after_ingest: Boolean(config.mark_seen_after_ingest !== false),
    classification_max_retries: Number(config.classification_max_retries || 3),
    oauth_tenant_id: String(config.oauth_tenant_id || ''),
    oauth_client_id: String(config.oauth_client_id || ''),
    oauth_client_secret: String(config.oauth_client_secret || ''),
    reply_smtp_override: Boolean(config.reply_smtp_override),
    reply_smtp_host: String(config.reply_smtp_host || ''),
    reply_smtp_port: Number(config.reply_smtp_port || 587),
    reply_smtp_username: String(config.reply_smtp_username || ''),
    reply_smtp_password: String(config.reply_smtp_password || ''),
    reply_smtp_from_email: String(config.reply_smtp_from_email || ''),
    reply_smtp_use_tls: Boolean(config.reply_smtp_use_tls !== false),
    sender_filter: (config.sender_filter || []).map((r) => ({
      domain: String(r.domain || ''),
      mode: r.mode === 'specific' ? 'specific' : 'all',
      addresses: Array.isArray(r.addresses) ? r.addresses.join(', ') : String(r.addresses || ''),
    })),
  };
}

function buildPayloadFromForm(form) {
  return {
    ...form,
    imap_port: Number(form.imap_port),
    poll_interval_minutes: Number(form.poll_interval_minutes),
    poll_interval_seconds: form.poll_interval_seconds != null ? Number(form.poll_interval_seconds) : null,
    classification_max_retries: Number(form.classification_max_retries),
    reply_smtp_port: Number(form.reply_smtp_port),
    sender_filter: (form.sender_filter || [])
      .filter((r) => String(r.domain || '').trim())
      .map((r) => ({
        domain: String(r.domain).trim().toLowerCase(),
        mode: r.mode,
        ...(r.mode === 'specific'
          ? { addresses: String(r.addresses || '').split(',').map((a) => a.trim().toLowerCase()).filter(Boolean) }
          : {}),
      })),
  };
}

function SyncStatusBadge({ status }) {
  if (!status?.configured) return null;
  const hasError = Boolean(status.last_sync_error);
  return (
    <div className={`alert ${hasError ? 'alert-warning' : 'alert-info'} py-2 px-3 small mb-3`}>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <span>
          <i className={`bi ${hasError ? 'bi-exclamation-triangle' : 'bi-arrow-repeat'} me-1`} />
          {status.enabled ? 'Sync active' : 'Sync disabled'}
        </span>
        {status.last_sync_at && (
          <span className="text-muted">
            Last sync: {new Date(status.last_sync_at).toLocaleString()}
          </span>
        )}
        <span className="text-muted">
          Emails synced: {status.emails_synced_total ?? 0}
        </span>
        {hasError && (
          <span className="text-danger ms-1">{status.last_sync_error}</span>
        )}
      </div>
    </div>
  );
}

function StepProgress({ step }) {
  const currentIdx = STEP_ORDER.indexOf(step);
  return (
    <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
      {STEP_ORDER.map((s, idx) => (
        <React.Fragment key={s}>
          <span className={`badge rounded-pill ${idx === currentIdx ? 'bg-primary' : idx < currentIdx ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
            {idx + 1}. {STEP_LABELS[s]}
          </span>
          {idx < STEP_ORDER.length - 1 && <i className="bi bi-chevron-right text-muted small" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function MailboxConfigForm() {
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState({ tone: '', message: '' });
  const [testState, setTestState] = useState({ loading: false, tone: '', message: '', tested: false });
  const [syncState, setSyncState] = useState({ loading: false, tone: '', message: '' });
  const [syncStatus, setSyncStatus] = useState(null);

  const [mode, setMode] = useState('wizard'); // 'wizard' | 'summary'
  const [step, setStep] = useState('provider');
  const [selectedProvider, setSelectedProvider] = useState('gmail');
  const [skipTest, setSkipTest] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [pollUnit, setPollUnit] = useState('minutes'); // 'minutes' | 'seconds' — display unit only

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Keeps poll_interval_minutes/poll_interval_seconds consistent no matter which
  // unit the user is currently editing in, so the saved payload always has a
  // sensible value in both fields regardless of which one is authoritative.
  const updatePollInterval = (rawValue, unit) => {
    if (unit === 'seconds') {
      const secs = Math.max(5, Math.round((Number(rawValue) || 5) / 5) * 5);
      setForm((prev) => ({ ...prev, poll_interval_seconds: secs, poll_interval_minutes: Math.max(1, Math.ceil(secs / 60)) }));
    } else {
      const mins = Math.max(1, Math.round(Number(rawValue) || 1));
      setForm((prev) => ({ ...prev, poll_interval_minutes: mins, poll_interval_seconds: null }));
    }
  };

  const handlePollUnitChange = (nextUnit) => {
    setPollUnit(nextUnit);
    setForm((prev) => {
      if (nextUnit === 'seconds') {
        const secs = Math.max(5, Math.round(((prev.poll_interval_seconds || prev.poll_interval_minutes * 60)) / 5) * 5);
        return { ...prev, poll_interval_seconds: secs };
      }
      const mins = Math.max(1, Math.round((prev.poll_interval_seconds || prev.poll_interval_minutes * 60) / 60));
      return { ...prev, poll_interval_minutes: mins, poll_interval_seconds: null };
    });
  };

  const updateConnection = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setTestState({ loading: false, tone: '', message: '', tested: false });
    setSkipTest(false);
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setFlash({ tone: '', message: '' });
    try {
      const [configRes, statusRes] = await Promise.all([
        getMailboxConfig(),
        getMailboxSyncStatus(),
      ]);
      const config = configRes?.config || null;
      const nextForm = buildFormFromConfig(config);
      setForm(nextForm);
      setPollUnit(nextForm.poll_interval_seconds != null ? 'seconds' : 'minutes');
      setSyncStatus(statusRes);
      const isConfigured = Boolean(nextForm.imap_host || nextForm.enabled);
      setMode(isConfigured ? 'summary' : 'wizard');
      if (isConfigured) setSelectedProvider(guessProviderId(nextForm.imap_host));
    } catch (err) {
      setFlash({ tone: 'danger', message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  async function handleSave() {
    setSaving(true);
    setFlash({ tone: '', message: '' });
    try {
      const res = await saveMailboxConfig(buildPayloadFromForm(form));
      setForm(buildFormFromConfig(res?.config || null));
      setFlash({ tone: 'success', message: 'Mailbox settings saved.' });
      setMode('summary');
    } catch (err) {
      setFlash({ tone: 'danger', message: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection() {
    setTestState({ loading: true, tone: '', message: '', tested: false });
    try {
      const res = await testMailboxConnection({
        auth_method: form.auth_method,
        imap_host: form.imap_host,
        imap_port: form.imap_port,
        imap_username: form.imap_username,
        imap_password: form.imap_password,
        imap_use_ssl: form.imap_use_ssl,
        imap_folder: form.imap_folder,
        oauth_tenant_id: form.oauth_tenant_id,
        oauth_client_id: form.oauth_client_id,
        oauth_client_secret: form.oauth_client_secret,
      });
      setTestState({ loading: false, tone: 'success', message: res?.detail || 'Connected successfully.', tested: true });
      setSkipTest(false);
    } catch (err) {
      setTestState({ loading: false, tone: 'danger', message: err.message, tested: false });
    }
  }

  async function handleSyncNow() {
    setSyncState({ loading: true, tone: '', message: '' });
    try {
      const res = await syncMailboxNow();
      setSyncState({ loading: false, tone: 'success', message: res?.detail || 'Sync started.' });
      setTimeout(async () => {
        try { setSyncStatus(await getMailboxSyncStatus()); } catch {}
      }, 3000);
    } catch (err) {
      setSyncState({ loading: false, tone: 'danger', message: err.message });
    }
  }

  // Sender filter helpers
  function addDomainRule() {
    setForm((prev) => ({
      ...prev,
      sender_filter: [...prev.sender_filter, { domain: '', mode: 'all', addresses: '' }],
    }));
  }
  function removeDomainRule(idx) {
    setForm((prev) => ({
      ...prev,
      sender_filter: prev.sender_filter.filter((_, i) => i !== idx),
    }));
  }
  function updateDomainRule(idx, field, value) {
    setForm((prev) => ({
      ...prev,
      sender_filter: prev.sender_filter.map((r, i) => i === idx ? { ...r, [field]: value } : r),
    }));
  }

  function selectProvider(id) {
    setSelectedProvider(id);
    const defaults = getProviderDefaults(id);
    setForm((prev) => {
      // Only apply provider defaults if the host is empty or still matches a known
      // provider default (untouched) — never clobber a manually-entered host/port.
      const isUntouched = !prev.imap_host || PROVIDERS.some((p) => p.defaults.imap_host && p.defaults.imap_host === prev.imap_host);
      if (!isUntouched) return prev;
      return { ...prev, imap_host: defaults.imap_host, imap_port: defaults.imap_port, imap_use_ssl: defaults.imap_use_ssl };
    });
  }

  function startWizard() {
    setMode('wizard');
    setStep('connect');
    setSelectedProvider(guessProviderId(form.imap_host));
    setSkipTest(false);
    setTestState({ loading: false, tone: '', message: '', tested: false });
    setAdvancedOpen(Boolean(form.sender_filter.length > 0 || form.reply_smtp_override));
  }

  function goToStep(next) {
    if (next === 'advanced') {
      setAdvancedOpen((prev) => prev || form.sender_filter.length > 0 || form.reply_smtp_override);
    }
    setStep(next);
  }

  const guidedProviderId = form.imap_host ? guessProviderId(form.imap_host) : selectedProvider;
  const canLeaveConnect = (testState.tested || skipTest) && Boolean(form.imap_host) && Boolean(form.imap_username);

  if (loading) {
    return (
      <div className="py-4 text-center text-muted small">
        <span className="spinner-border spinner-border-sm me-2" />Loading mailbox settings…
      </div>
    );
  }

  const flashBanner = flash.message && (
    <div className={`alert alert-${flash.tone} alert-dismissible small py-2`} role="alert">
      {flash.message}
      <button type="button" className="btn-close" onClick={() => setFlash({ tone: '', message: '' })} />
    </div>
  );

  // ── Summary view (already configured) ──
  if (mode === 'summary') {
    return (
      <div>
        {flashBanner}
        <SyncStatusBadge status={syncStatus} />
        <div className="card mb-3">
          <div className="card-header py-2 fw-semibold small bg-light d-flex align-items-center justify-content-between">
            <span>Mailbox configuration</span>
            <span className={`badge ${form.enabled ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
              {form.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="card-body">
            <dl className="row small mb-0">
              <dt className="col-sm-3 text-muted">Host</dt>
              <dd className="col-sm-9">{form.imap_host || '—'}:{form.imap_port}</dd>
              <dt className="col-sm-3 text-muted">Username</dt>
              <dd className="col-sm-9">{form.imap_username || '—'}</dd>
              <dt className="col-sm-3 text-muted">Folder</dt>
              <dd className="col-sm-9">{form.imap_folder}</dd>
              <dt className="col-sm-3 text-muted">Authentication</dt>
              <dd className="col-sm-9">{form.auth_method === 'oauth2' ? 'OAuth2 (Microsoft 365)' : 'Basic Auth (Password)'}</dd>
            </dl>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="mailbox-enabled-summary"
              checked={form.enabled}
              onChange={(e) => update('enabled', e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="mailbox-enabled-summary">Incoming email sync enabled</label>
          </div>
          <button type="button" className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={syncState.loading || !syncStatus?.enabled}
            onClick={handleSyncNow}
            title={!syncStatus?.enabled ? 'Enable sync to trigger manually' : ''}
          >
            {syncState.loading
              ? <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
              : <><i className="bi bi-arrow-repeat me-1" />Sync Now</>}
          </button>
          {syncState.message && (
            <span className={`small text-${syncState.tone}`}>{syncState.message}</span>
          )}
          <div className="ms-auto d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadConfig} disabled={loading || saving}>
              Refresh
            </button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={startWizard}>
              <i className="bi bi-pencil me-1" />Edit configuration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard view ──
  return (
    <div>
      {flashBanner}
      <StepProgress step={step} />

      {step === 'provider' && (
        <div className="card mb-3">
          <div className="card-header py-2 fw-semibold small bg-light">Which mailbox provider are you connecting?</div>
          <div className="card-body">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`btn ${selectedProvider === p.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => selectProvider(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="small text-muted mb-3">
              This prefills the host, port, and SSL settings for the next step — you can still change any of them.
            </p>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => goToStep('connect')}>
              Next <i className="bi bi-chevron-right ms-1" />
            </button>
          </div>
        </div>
      )}

      {step === 'connect' && (
        <>
          <ProviderGuidePanel providerId={guidedProviderId} />
          <div className="card mb-3">
            <div className="card-header py-2 fw-semibold small bg-light">Connection details</div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label small fw-semibold">Authentication method</label>
                <div className="btn-group d-flex" role="group" style={{ maxWidth: 380 }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.auth_method === 'basic' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => updateConnection('auth_method', 'basic')}
                  >
                    <i className="bi bi-key me-1" />Basic Auth (Password)
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.auth_method === 'oauth2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => updateConnection('auth_method', 'oauth2')}
                  >
                    <i className="bi bi-shield-lock me-1" />OAuth2 (Microsoft 365)
                  </button>
                </div>
              </div>

              {form.auth_method === 'oauth2' && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <p className="small text-muted mb-2">
                    <i className="bi bi-info-circle me-1" />
                    Register an app in Azure AD with <strong>IMAP.AccessAsApp</strong> permission and admin consent.
                    No redirect URI needed.
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small">Tenant ID</label>
                      <input
                        className="form-control form-control-sm font-monospace"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={form.oauth_tenant_id}
                        onChange={(e) => updateConnection('oauth_tenant_id', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small">Client ID (Application ID)</label>
                      <input
                        className="form-control form-control-sm font-monospace"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={form.oauth_client_id}
                        onChange={(e) => updateConnection('oauth_client_id', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small">Client Secret</label>
                      <input
                        className="form-control form-control-sm"
                        type="password"
                        autoComplete="new-password"
                        placeholder={form.oauth_client_secret === '***' ? '(saved)' : 'Paste secret value'}
                        value={form.oauth_client_secret}
                        onChange={(e) => updateConnection('oauth_client_secret', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="row g-3">
                <div className="col-sm-8">
                  <label className="form-label small">IMAP Host</label>
                  <input className="form-control form-control-sm" placeholder="imap.gmail.com" value={form.imap_host} onChange={(e) => updateConnection('imap_host', e.target.value)} />
                </div>
                <div className="col-sm-4">
                  <label className="form-label small">Port</label>
                  <input className="form-control form-control-sm" type="number" min="1" max="65535" value={form.imap_port} onChange={(e) => updateConnection('imap_port', e.target.value)} />
                </div>
                <div className="col-sm-8">
                  <label className="form-label small">Username / Email address</label>
                  <input className="form-control form-control-sm" placeholder="orders@yourcompany.com" value={form.imap_username} onChange={(e) => updateConnection('imap_username', e.target.value)} />
                </div>
                {form.auth_method !== 'oauth2' && (
                  <div className="col-sm-4">
                    <label className="form-label small">Password</label>
                    <input className="form-control form-control-sm" type="password" autoComplete="new-password" placeholder={form.imap_password === '***' ? '(saved)' : ''} value={form.imap_password} onChange={(e) => updateConnection('imap_password', e.target.value)} />
                  </div>
                )}
                <div className="col-sm-8">
                  <label className="form-label small">Folder to watch</label>
                  <input className="form-control form-control-sm" placeholder="INBOX" value={form.imap_folder} onChange={(e) => updateConnection('imap_folder', e.target.value)} />
                </div>
                <div className="col-sm-4">
                  <label className="form-label small">Poll every</label>
                  <div className="input-group input-group-sm">
                    <input
                      className="form-control"
                      type="number"
                      min={pollUnit === 'seconds' ? 5 : 1}
                      step={pollUnit === 'seconds' ? 5 : 1}
                      max={pollUnit === 'seconds' ? 3600 : 60}
                      value={pollUnit === 'seconds' ? (form.poll_interval_seconds ?? 5) : form.poll_interval_minutes}
                      onChange={(e) => updatePollInterval(e.target.value, pollUnit)}
                    />
                    <select
                      className="form-select"
                      style={{ maxWidth: 110 }}
                      value={pollUnit}
                      onChange={(e) => handlePollUnitChange(e.target.value)}
                    >
                      <option value="minutes">Minutes</option>
                      <option value="seconds">Seconds</option>
                    </select>
                  </div>
                  {pollUnit === 'seconds' && (
                    <div className="form-text">Minimum 5 seconds, in 5-second increments.</div>
                  )}
                </div>
                <div className="col-12 d-flex flex-wrap gap-4">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="imap-ssl" checked={form.imap_use_ssl} onChange={(e) => updateConnection('imap_use_ssl', e.target.checked)} />
                    <label className="form-check-label small" htmlFor="imap-ssl">Use SSL/TLS</label>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="imap-seen" checked={form.mark_seen_after_ingest} onChange={(e) => update('mark_seen_after_ingest', e.target.checked)} />
                    <label className="form-check-label small" htmlFor="imap-seen">Mark emails as read after ingestion</label>
                  </div>
                </div>
                <div className="col-sm-4">
                  <label className="form-label small">Max classification retries</label>
                  <input className="form-control form-control-sm" type="number" min="1" max="10" value={form.classification_max_retries} onChange={(e) => update('classification_max_retries', e.target.value)} />
                  <div className="form-text">After this many failures, email is flagged as Needs Review.</div>
                </div>
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={testState.loading || !form.imap_host || !form.imap_username}
                  onClick={handleTestConnection}
                >
                  {testState.loading
                    ? <><span className="spinner-border spinner-border-sm me-1" />Testing…</>
                    : <><i className="bi bi-plug me-1" />Test Connection</>}
                </button>
                {testState.message && (
                  <div className={`alert alert-${testState.tone} py-2 px-3 mt-2 small mb-0`}>
                    <div>
                      {testState.tone === 'success'
                        ? <i className="bi bi-check-circle me-1" />
                        : <i className="bi bi-x-circle me-1" />}
                      {testState.message}
                    </div>
                    {testState.tone === 'danger' && (
                      <div className="text-muted mt-1"><i className="bi bi-lightbulb me-1" />{guidanceForError(testState.message)}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 mb-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => goToStep('provider')}>
              <i className="bi bi-chevron-left me-1" />Back
            </button>
            {!canLeaveConnect && Boolean(form.imap_host) && Boolean(form.imap_username) && (
              <button type="button" className="btn btn-sm btn-link text-muted" onClick={() => { setSkipTest(true); }}>
                Continue without testing
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary ms-auto"
              disabled={!canLeaveConnect}
              title={!canLeaveConnect ? 'Test the connection first, or choose "Continue without testing"' : ''}
              onClick={() => goToStep('advanced')}
            >
              Next <i className="bi bi-chevron-right ms-1" />
            </button>
          </div>
        </>
      )}

      {step === 'advanced' && (
        <>
          <div className="card mb-3">
            <div className="card-header py-2 fw-semibold small bg-light d-flex align-items-center justify-content-between">
              <span>Advanced settings (optional)</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setAdvancedOpen((v) => !v)}>
                {advancedOpen ? 'Hide' : 'Configure'} <i className={`bi bi-chevron-${advancedOpen ? 'up' : 'down'} ms-1`} />
              </button>
            </div>
            {!advancedOpen && (
              <div className="card-body py-2">
                <p className="small text-muted mb-0">
                  Sender security ({form.sender_filter.length === 0 ? 'all senders allowed' : `${form.sender_filter.length} domain rule(s)`}) and reply SMTP override
                  ({form.reply_smtp_override ? 'custom' : 'using global SMTP'}). Most setups can skip this.
                </p>
              </div>
            )}
            {advancedOpen && (
              <div className="card-body">
                <div className="mb-4">
                  <p className="fw-semibold small mb-1">Sender Security</p>
                  <p className="small text-muted mb-3">
                    Leave empty to accept emails from all senders. Add domain rules to restrict which senders are processed.
                    Blocked emails are stored in the inbox for visibility but never classified or sent to OrderX.
                  </p>
                  {form.sender_filter.map((rule, idx) => (
                    <div key={idx} className="border rounded p-3 mb-2 bg-light">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Domain (e.g. acme.com)"
                          value={rule.domain}
                          onChange={(e) => updateDomainRule(idx, 'domain', e.target.value)}
                          style={{ maxWidth: 220 }}
                        />
                        <div className="btn-group btn-group-sm flex-shrink-0" role="group" aria-label="Mode">
                          <button
                            type="button"
                            className={`btn ${rule.mode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => updateDomainRule(idx, 'mode', 'all')}
                            title="Process all emails from this domain"
                          >
                            All emails
                          </button>
                          <button
                            type="button"
                            className={`btn ${rule.mode === 'specific' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => updateDomainRule(idx, 'mode', 'specific')}
                            title="Process only specific from-addresses"
                          >
                            Specific
                          </button>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-auto flex-shrink-0"
                          onClick={() => removeDomainRule(idx)}
                          aria-label="Remove rule"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      {rule.mode === 'specific' && (
                        <div>
                          <label className="form-label small text-muted mb-1">
                            Allowed from-addresses (comma-separated)
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="buyer@acme.com, orders@acme.com"
                            value={rule.addresses}
                            onChange={(e) => updateDomainRule(idx, 'addresses', e.target.value)}
                          />
                          <div className="form-text">Only these exact addresses from {rule.domain || 'this domain'} will be processed.</div>
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={addDomainRule}>
                    <i className="bi bi-plus-lg me-1" />Add domain
                  </button>
                </div>

                <div>
                  <div className="form-check form-switch mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="reply-smtp-override"
                      checked={form.reply_smtp_override}
                      onChange={(e) => update('reply_smtp_override', e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold small" htmlFor="reply-smtp-override">Use a different SMTP for quote-ready replies</label>
                  </div>
                  {form.reply_smtp_override && (
                    <div className="row g-3">
                      <div className="col-sm-8">
                        <label className="form-label small">SMTP Host</label>
                        <input className="form-control form-control-sm" value={form.reply_smtp_host} onChange={(e) => update('reply_smtp_host', e.target.value)} />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small">Port</label>
                        <input className="form-control form-control-sm" type="number" value={form.reply_smtp_port} onChange={(e) => update('reply_smtp_port', e.target.value)} />
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label small">Username</label>
                        <input className="form-control form-control-sm" value={form.reply_smtp_username} onChange={(e) => update('reply_smtp_username', e.target.value)} />
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label small">Password</label>
                        <input className="form-control form-control-sm" type="password" autoComplete="new-password" placeholder={form.reply_smtp_password === '***' ? '(saved)' : ''} value={form.reply_smtp_password} onChange={(e) => update('reply_smtp_password', e.target.value)} />
                      </div>
                      <div className="col-sm-8">
                        <label className="form-label small">From email address</label>
                        <input className="form-control form-control-sm" type="email" value={form.reply_smtp_from_email} onChange={(e) => update('reply_smtp_from_email', e.target.value)} />
                      </div>
                      <div className="col-sm-4 d-flex align-items-end pb-1">
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" id="reply-smtp-tls" checked={form.reply_smtp_use_tls} onChange={(e) => update('reply_smtp_use_tls', e.target.checked)} />
                          <label className="form-check-label small" htmlFor="reply-smtp-tls">Use TLS</label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mb-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => goToStep('connect')}>
              <i className="bi bi-chevron-left me-1" />Back
            </button>
            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => goToStep('review')}>
              Next <i className="bi bi-chevron-right ms-1" />
            </button>
          </div>
        </>
      )}

      {step === 'review' && (
        <>
          <div className="card mb-3">
            <div className="card-header py-2 fw-semibold small bg-light">Review & save</div>
            <div className="card-body">
              {!testState.tested && (
                <div className="alert alert-warning py-2 px-3 small mb-3">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Connection has not been successfully tested. You can still save, but sync may fail until it's confirmed working.
                </div>
              )}
              <dl className="row small mb-3">
                <dt className="col-sm-3 text-muted">Host</dt>
                <dd className="col-sm-9">{form.imap_host || '—'}:{form.imap_port}</dd>
                <dt className="col-sm-3 text-muted">Username</dt>
                <dd className="col-sm-9">{form.imap_username || '—'}</dd>
                <dt className="col-sm-3 text-muted">Folder</dt>
                <dd className="col-sm-9">{form.imap_folder}</dd>
                <dt className="col-sm-3 text-muted">Authentication</dt>
                <dd className="col-sm-9">{form.auth_method === 'oauth2' ? 'OAuth2 (Microsoft 365)' : 'Basic Auth (Password)'}</dd>
                <dt className="col-sm-3 text-muted">Sender security</dt>
                <dd className="col-sm-9">{form.sender_filter.length === 0 ? 'All senders allowed' : `${form.sender_filter.length} domain rule(s)`}</dd>
                <dt className="col-sm-3 text-muted">Reply SMTP</dt>
                <dd className="col-sm-9">{form.reply_smtp_override ? 'Custom override' : 'Using global SMTP'}</dd>
              </dl>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="mailbox-enabled"
                  checked={form.enabled}
                  onChange={(e) => update('enabled', e.target.checked)}
                />
                <label className="form-check-label fw-semibold" htmlFor="mailbox-enabled">
                  Enable incoming email sync
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2 border-top pt-3 mt-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => goToStep('advanced')}>
              <i className="bi bi-chevron-left me-1" />Back
            </button>
            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save settings'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
